import { utils } from '@kurtosys/ksys-app-template';
import { IQueryField } from '../../../models/commonTypes';
import { AppStoreBase } from './AppStoreBase';
import { IAppComponents } from '../models/IAppComponents';
import { computed } from 'mobx';
import { StoreContext } from '../../../configuration/StoreContext';
import { ICardProps } from '@kurtosys/ksys-app-components/dist/components/base/Card/models';
import { IDocumentList } from '../models/IDocumentList';
import { DocumentListStore } from './DocumentListStore';
import { DownloadStore } from './DownloadStore';
import { Manifest } from '../../../configuration/Manifest';
import { IDisclaimersProps } from '@kurtosys/ksys-app-components/dist/components/overview/Disclaimers/models';

/* [Component: appStoreComponentImport] */

export class AppStore extends AppStoreBase {
	constructor(element: HTMLElement, url: string, storeContext: StoreContext, manifest: Manifest) {
		super(element, url, storeContext, manifest);
	}

	@computed
	get hasData(): boolean {
		return this.hasDocuments;
	}

	@computed
	get noDataPlaceholder(): string | undefined {
		return this.appComponentConfiguration && this.appComponentConfiguration.noDataOptions && this.appComponentConfiguration.noDataOptions.noDataPlaceholder || this.noDocumentsText;
	}

	@computed
	get documentListStores(): DocumentListStore[] {
		return this.documentLists.map((documentList: IDocumentList) => {
			const documentFields = (documentList && documentList.documentFields) || {};
			const fields = Object.entries(documentFields).map(([key, value]) => {
				return {
					name: key,
					...value as object,
				} as IQueryField;
			});
			for (const documentQuery of documentList.documentQueries || []) {
				documentQuery.fields = fields;
			}

			return new DocumentListStore(documentList, this.storeContext);
		});
	}

	@computed
	get downloadStore(): DownloadStore {
		return new DownloadStore(this.storeContext);
	}

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get showDownloadAll(): boolean {
		if (this.appComponentConfiguration && !utils.typeChecks.isNullOrUndefined(this.appComponentConfiguration.showDownloadAll)) {
			return this.appComponentConfiguration.showDownloadAll;
		}
		return true;
	}

	@computed
	get showDownloadSelectedWhenEmpty(): boolean {
		if (this.appComponentConfiguration && !utils.typeChecks.isNullOrUndefined(this.appComponentConfiguration.showDownloadSelectedWhenEmpty)) {
			return this.appComponentConfiguration.showDownloadSelectedWhenEmpty;
		}
		return true;
	}

	@computed
	get downloadAllLabel(): string {
		const { translationStore } = this.storeContext;
		const configDownloadAllLabel = this.appComponentConfiguration && this.appComponentConfiguration.downloadAllLabel;
		return this.downloadStore.isLoading ? `${ translationStore.translate('Downloading') }...` : configDownloadAllLabel || '';
	}

	@computed
	get downloadSelectedLabel(): string {
		const { translationStore } = this.storeContext;
		const configdownloadSelectedLabel = this.appComponentConfiguration && this.appComponentConfiguration.downloadSelectedLabel || 'Download {selected}';
		const values = {
			selected: this.selectedDocumentIds.length,
		};
		const label = utils.replacePlaceholders(values, configdownloadSelectedLabel);
		return this.downloadStore.isLoading ? `${ translationStore.translate('Downloading') }...` : label;
	}

	get useSpinnerConfig(): boolean {
		const configUseSpinner = this.appComponentConfiguration && this.appComponentConfiguration.useSpinner;
		return configUseSpinner ? configUseSpinner : false;
	}

	@computed
	get documentIds(): string[] {
		const ids: string[] = [];
		for (const docListStore of this.documentListStores) {
			ids.push(...docListStore.documentIds);
		}
		return ids;
	}

	@computed
	get hasDocuments(): boolean {
		return this.documentListStores.reduce((acc: boolean, documentListStore) => acc || documentListStore.hasDocuments, false);
	}

	@computed
	get hasSelectedDocuments(): boolean {
		return this.documentListStores.reduce((acc: boolean, documentListStore) => acc || documentListStore.hasSelectedDocuments, false);
	}

	@computed
	get hasAllowSelection(): boolean {
		return this.documentLists.reduce((acc: boolean, documentLists) => acc || documentLists.allowSelection || false, false);
	}

	@computed
	get selectedDocumentIds(): string[] {
		const ids: string[] = [];
		this.documentListStores.map((documentListStore) => {
			ids.push(...documentListStore.selectedDocumentIds);
		});
		const valueFn = (i: string) => ids[ids.indexOf(i)];
		return utils.collection.unique(ids, valueFn);
	}

	@computed
	get noDocumentsText(): string {
		const noDocumentsTextQueryOptions = this.appComponentConfiguration && this.appComponentConfiguration.noDocumentsText;
		return this.getQueryValue(noDocumentsTextQueryOptions);
	}

	@computed
	get hideListsWhenNoDocuments(): boolean {
		return !!(this.appComponentConfiguration && this.appComponentConfiguration.hideListsWhenNoDocuments);
	}

	@computed
	get showDocumentDownloadLists(): boolean {
		if (!this.hasDocuments && this.hideListsWhenNoDocuments) {
			return false;
		}
		return true;
	}

	get disclaimersProps(): IDisclaimersProps | undefined {
		const disclaimersProps = this.appComponentConfiguration && this.appComponentConfiguration.disclaimersProps;
		return this.mergeQueriesAndProps(disclaimersProps);
	}

	@computed
	get documentLists(): IDocumentList[] {
		return (this.appComponentConfiguration && this.appComponentConfiguration.documentLists) || [];
	}

}